import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient, type QueryCacheNotifyEvent } from "@tanstack/react-query";

import { LABOR_OPEN_SESSION_QUERY_KEY } from "@/components/LaborActiveSessionProvider";
import { useAuth } from "@/components/AuthProvider";
import { ApiError } from "@/lib/api";
import { invalidateIntegratorLaborMenuQueries } from "@/lib/hubLaborActiveSession";
import { isHubLaborPresenceEvent, useHubLaborPresenceStream } from "@/lib/useHubLaborPresenceStream";
import type { HubCacheSseEvent } from "@/lib/hubCacheChannels";
import { useIntegratorMe } from "@/lib/me-query";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { useNowTick } from "@/lib/useNowTick";
import { laborPageKeysEquivalent } from "@/pages/clampqc/clampQcLaborClockState";

import {
  activeOnOtherPageFromPayload,
  fetchLaborActiveSessionResolvingDeparture,
} from "./laborActiveSessionConflict";
import { formatLaborLocationLabel } from "./laborPageKey";
import { claimLaborReadOnlyStay, clearLaborReadOnlyStay, isLaborReadOnlyStayActive } from "./laborReadOnlyStay";
import { isLaborOwnerDeparting } from "./laborOwnerDeparture";
import { isLaborSessionTombstoned } from "./laborEndedSessionTombstone";
import { waitForLaborOwnerDepartureComplete } from "./laborOwnerDepartureWait";
import { clearLaborDevCountdown, writeLaborDevCountdown } from "./laborDevCountdown";
import {
  clearLaborDevShellLock,
  LABOR_DEV_SHELL_STORAGE_KEY,
  readLaborDevShellLock,
  setLaborDevShellLock,
} from "./laborDevShellLock";
import { LABOR_OPEN_SESSION_SYNC_STORAGE_KEY, subscribeLaborOpenSessionSync } from "./laborOpenSessionSync";
import { endOpenLaborServerSessionIfAny } from "./laborReleaseHubPresence";
import { readOpenLaborSessionPayloadFromCache, writeOpenLaborSessionCache } from "./laborOpenSessionCache";
import {
  laborHeartbeatResponseSessionClosed,
  laborPresenceEventShouldEndLocalSession,
  openSessionPayloadEndsLocalSession,
  openSessionRowFromPayload,
} from "./laborRemoteSessionEndSync";
import {
  endLaborSession,
  fetchLaborActiveSession,
  fetchLaborOpenSession,
  recordLaborScan,
  sendLaborHeartbeat,
  startLaborSession,
  type LaborActiveSessionPayload,
  type LaborScan,
  type LaborSession,
} from "./laborApi";
import { parseLaborSessionRow } from "./parseLaborSession";
import { getLaborTabId, LABOR_TAB_ID_NO_STORAGE } from "./laborTabId";
import { useLaborPersistenceOverride } from "./laborEnv";
import type { LaborBannerState } from "./LaborStatusPanel";
import type { LaborDomain, LaborTargetDescriptor } from "./laborTarget";
import { useLaborAttentionActivity } from "./useLaborAttentionActivity";
import { playLaborIdleWarningSound } from "@/lib/floorStationPairAudio";

export const FALLBACK_LABOR_HEARTBEAT_INTERVAL_MS = 30_000;
/** Fallback until `/api/labor/active-session` returns `idle_timeout_ms` from `labor.idle_timeout_ms()`. */
export const FALLBACK_LABOR_IDLE_TIMEOUT_MS = 900_000;
/** Play a warning tone after this fraction of idle time (before clock-out at idle_timeout_ms). */
function laborIdleWarningFromTimeoutMs(idleTimeoutMs: number): number {
  return Math.floor((idleTimeoutMs * 2) / 3);
}

export type LaborIdleTickAction = "clock_out" | "warn" | "heartbeat" | "wait";

/**
 * Returns true when a failed `/api/labor/heartbeat` response means the session or auth
 * cannot continue locally (401/403). Transient transport or 5xx errors return false so
 * a brief warehouse Wi-Fi drop or paused tablet WebView does not self-terminate an open session.
 */
export function isFatalLaborHeartbeatError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

/**
 * Idle stations clock out after the idle window with no activity.
 * Stations that disable idle timeout keep heartbeating so presence stays current.
 */
export function laborIdleTickAction(input: {
  disableIdleTimeout: boolean;
  hasPendingActivity: boolean;
  idleSinceHeartbeatMs: number;
  idleTimeoutMs: number;
  idleWarningAlreadyPlayed: boolean;
}): LaborIdleTickAction {
  if (input.disableIdleTimeout || input.hasPendingActivity) {
    return "heartbeat";
  }
  if (input.idleSinceHeartbeatMs >= input.idleTimeoutMs) {
    return "clock_out";
  }
  if (
    !input.idleWarningAlreadyPlayed
    && input.idleSinceHeartbeatMs >= laborIdleWarningFromTimeoutMs(input.idleTimeoutMs)
  ) {
    return "warn";
  }
  return "wait";
}

function flushLocalLaborHeartbeatMirror(input: {
  page: string;
  startedAtIso: string;
  idleTimeoutMs: number;
  lastActivityMsRef: { current: number };
  lastActivityAcknowledgedMsRef: { current: number };
  lastDbHeartbeatAtMsRef: { current: number };
}): void {
  input.lastActivityAcknowledgedMsRef.current = input.lastActivityMsRef.current;
  input.lastDbHeartbeatAtMsRef.current = Date.now();
  if (import.meta.env.DEV) {
    writeLaborDevCountdown({
      page: input.page,
      startedAtMs: new Date(input.startedAtIso).getTime(),
      lastActivityMs: input.lastActivityMsRef.current,
      lastDbHeartbeatAtMs: input.lastDbHeartbeatAtMsRef.current,
      idleTimeoutMs: input.idleTimeoutMs,
      isWorking: true,
      updatedAtMs: Date.now(),
    });
  }
}

export interface LaborSessionConfig {
  page: string;
  domain: LaborDomain;
  moduleGate: string;
  devPersistenceDisabled: () => boolean;
  workLabel: string;
  autoStart?: boolean;
  restartAfterManualStop?: boolean;
  onGateReady?: (ready: boolean) => void;
  onIdleTimeout?: (context: { netSeconds: number }) => void;
  inactiveStartMessage?: string;
  localOnlyMessage?: (reason: "dev" | "writes-blocked" | "read-only") => string;
  /**
   * When true (default), pointer, keys, scroll, wheel, and focus anywhere in the window
   * count as activity (throttled). Heartbeats flush on the heartbeat interval only when there
   * is new activity since the last acknowledged flush, matching `labor.device_session.last_heartbeat_at`
   * (local-only dev mirrors that flush in-process on the same interval).
   */
  trackUserAttention?: boolean;
  /**
   * When true with server labor persistence, `gateReady` only becomes true after a positive
   * `labor.device_session` id is present. Local-only timers (read-only, writes blocked, dev) do not satisfy the gate.
   */
  requirePersistedClockIn?: boolean;
  /**
   * Called after repeated heartbeat failures or an auth-style failure while a persisted session was active.
   * Use to navigate away from job workspaces that must stay paired with an open server session.
   */
  onPersistedSessionLost?: () => void;
  /** Called when the open session row disappeared while this page still believed it was clocked in (for example clock out from another tab). */
  onSessionEndedRemotely?: () => void;
  /** Called after a successful manual clock-out (`stopWorking`) before optional `restartAfterManualStop`. */
  onStopped?: () => void;
  /**
   * When true, inactivity does not clock the session out and does not play the idle warning.
   * Heartbeats still run on the interval. The server idle sweep must also skip this station.
   */
  disableIdleTimeout?: boolean;
}

export interface LaborSessionController {
  gateReady: boolean;
  isWorking: boolean;
  loading: boolean;
  session: LaborSession | null;
  sessionId: number | null;
  scans: LaborScan[];
  elapsedSeconds: number;
  heartbeatIntervalMs: number;
  idleTimeoutMs: number;
  banner: LaborBannerState | null;
  lastSummary: { netSeconds: number; idleTimeout: boolean; deviceCount?: number } | null;
  activeOnOtherPage: string | null;
  touchActivity: () => void;
  beginWorking: () => Promise<void>;
  stopWorking: () => Promise<void>;
  recordScan: (input: { deviceSerial: string; target: LaborTargetDescriptor }) => Promise<void>;
  resumeOpenSessionFromServer: () => Promise<boolean>;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function numberFromUnknown(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function responseWritesBlocked(payload: Record<string, unknown> | null): boolean | undefined {
  if (payload === null) return undefined;
  if (typeof payload.writesBlocked === "boolean") return payload.writesBlocked;
  if (typeof payload.writeFirebreak === "boolean") return payload.writeFirebreak;
  return undefined;
}

function formatLocalOnlyMessage(
  config: LaborSessionConfig,
  reason: "dev" | "writes-blocked" | "read-only",
): string {
  if (config.localOnlyMessage) {
    return config.localOnlyMessage(reason);
  }
  if (reason === "dev") {
    return `Started working. Using a local ${config.workLabel} timer; nothing is persisted to the labor tables on the server in this browser profile.`;
  }
  if (reason === "read-only") return `Started working. Your role is read-only here, so this is a local timer only.`;
  return `Started working. Labor writes are blocked, so this is a local timer only.`;
}

export function useLaborSession(config: LaborSessionConfig): LaborSessionController {
  const queryClient = useQueryClient();
  const { loading: authLoading, session: authSession } = useAuth();
  const meQuery = useIntegratorMe();
  const writesAllowed = useHubModuleWrite(config.moduleGate);
  const laborPersistenceOverride = useLaborPersistenceOverride();
  const [heartbeatIntervalMs, setHeartbeatIntervalMs] = useState(FALLBACK_LABOR_HEARTBEAT_INTERVAL_MS);
  const [idleTimeoutMs, setIdleTimeoutMs] = useState(FALLBACK_LABOR_IDLE_TIMEOUT_MS);
  const [session, setSession] = useState<LaborSession | null>(null);
  const [scans, setScans] = useState<LaborScan[]>([]);
  const [laborWritesBlocked, setLaborWritesBlocked] = useState(true);
  const [gateReady, setGateReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeOnOtherPage, setActiveOnOtherPage] = useState<string | null>(null);
  const [banner, setBanner] = useState<LaborBannerState | null>(null);
  const [lastSummary, setLastSummary] = useState<{ netSeconds: number; idleTimeout: boolean; deviceCount?: number } | null>(null);

  const lastActivityMsRef = useRef(Date.now());
  /** Wall clock of the last successful heartbeat flush (`last_heartbeat_at` semantics). */
  const lastDbHeartbeatAtMsRef = useRef(Date.now());
  /** Activity timestamp (`lastActivityMsRef`) already covered by the last flush. */
  const lastActivityAcknowledgedMsRef = useRef(Date.now());
  const heartbeatFailureStreakRef = useRef(0);
  const idleWarningPlayedRef = useRef(false);
  const sessionRef = useRef<LaborSession | null>(null);
  const writesBlockedRef = useRef(true);
  /** Latest write tier from `/api/me`; refreshed every render for async labor bootstrap after profile loads. */
  const writesAllowedRef = useRef(writesAllowed);
  writesAllowedRef.current = writesAllowed;
  const pageRef = useRef(config.page);
  const configRef = useRef(config);
  const finalizeRef = useRef<(idleTimeout: boolean) => void>(() => {});

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    writesBlockedRef.current =
      laborWritesBlocked || !writesAllowedRef.current || configRef.current.devPersistenceDisabled();
  }, [config, laborWritesBlocked, writesAllowed]);

  useEffect(() => {
    config.onGateReady?.(gateReady);
  }, [config, gateReady]);

  const clockMs = useNowTick(session !== null);
  const elapsedSeconds =
    session === null ? 0 : Math.max(0, Math.floor((clockMs - new Date(session.started_at).getTime()) / 1000));

  const invalidate = useCallback(() => {
    invalidateIntegratorLaborMenuQueries(queryClient);
  }, [queryClient]);

  const mirrorPersistedOpenSession = useCallback(
    (session: LaborSession | null) => {
      if (session !== null && session.id > 0) {
        writeOpenLaborSessionCache(queryClient, session);
        return;
      }
      writeOpenLaborSessionCache(queryClient, null);
    },
    [queryClient],
  );

  const laborGateOpenForConfiguredPersistence = useCallback((hasPersistedServerSession: boolean): boolean => {
    const cfg = configRef.current;
    if (cfg.requirePersistedClockIn !== true || cfg.devPersistenceDisabled()) {
      return true;
    }
    return hasPersistedServerSession;
  }, []);

  const handlePersistedSessionLostFromHeartbeat = useCallback(async (): Promise<void> => {
    const current = sessionRef.current;
    const currentConfig = configRef.current;
    heartbeatFailureStreakRef.current = 0;
    if (current === null || current.id <= 0) {
      return;
    }
    if (!currentConfig.devPersistenceDisabled()) {
      try {
        await endLaborSession({
          sessionId: current.id,
          idleTimeout: false,
          domain: currentConfig.domain,
        });
      } catch {
        /* session may already be closed on the server */
      }
    }
    sessionRef.current = null;
    setSession(null);
    setScans([]);
    setGateReady(false);
    mirrorPersistedOpenSession(null);
    if (typeof currentConfig.onPersistedSessionLost === "function") {
      setBanner(null);
    } else {
      setBanner({
        tone: "warning",
        message:
          "Lost contact with the open labor session on the server. Stop and open the job again when the network is stable.",
      });
    }
    invalidate();
    if (import.meta.env.DEV) {
      clearLaborDevCountdown(currentConfig.page);
    }
    if (isLaborOwnerDeparting()) {
      return;
    }
    currentConfig.onPersistedSessionLost?.();
  }, [invalidate, mirrorPersistedOpenSession]);

  const touchActivity = useCallback(() => {
    const now = Date.now();
    idleWarningPlayedRef.current = false;
    lastActivityMsRef.current = now;
    const current = sessionRef.current;
    const currentConfig = configRef.current;
    if (import.meta.env.DEV && current !== null) {
      writeLaborDevCountdown({
        page: currentConfig.page,
        startedAtMs: new Date(current.started_at).getTime(),
        lastActivityMs: now,
        lastDbHeartbeatAtMs: lastDbHeartbeatAtMsRef.current,
        idleTimeoutMs,
        isWorking: true,
        updatedAtMs: now,
      });
    }
  }, [idleTimeoutMs]);

  const handleSessionEndedRemotely = useCallback((options?: { idleTimeout?: boolean }) => {
    const endedSession = sessionRef.current;
    if (endedSession === null) {
      return;
    }
    const cfg = configRef.current;
    const netSeconds = Math.max(
      0,
      Math.floor((Date.now() - new Date(endedSession.started_at).getTime()) / 1000),
    );
    heartbeatFailureStreakRef.current = 0;
    sessionRef.current = null;
    setSession(null);
    setScans([]);
    setGateReady(false);
    mirrorPersistedOpenSession(null);
    if (options?.idleTimeout === true) {
      setBanner({
        tone: "warning",
        message: "Clocked out due to inactivity on the server.",
      });
    } else {
      setBanner(null);
    }
    invalidate();
    if (import.meta.env.DEV) {
      clearLaborDevCountdown(cfg.page);
    }
    if (isLaborOwnerDeparting()) {
      return;
    }
    if (options?.idleTimeout === true) {
      cfg.onIdleTimeout?.({ netSeconds });
      return;
    }
    if (claimLaborReadOnlyStay({ page: cfg.page, tabId: getLaborTabId() })) {
      return;
    }
    cfg.onSessionEndedRemotely?.();
  }, [invalidate, mirrorPersistedOpenSession]);

  const reconcileRemoteOpenSession = useCallback(
    async (hint?: HubCacheSseEvent): Promise<void> => {
      const mine = sessionRef.current;
      if (mine === null) {
        return;
      }

      const cfg = configRef.current;
      const idleTimeout = hint?.labor_idle_timeout === true;

      if (mine.id <= 0) {
        const lock = readLaborDevShellLock();
        const lockStillOwnsThisPage = lock !== null && laborPageKeysEquivalent(lock.page, cfg.page);
        if (!lockStillOwnsThisPage && sessionRef.current?.id === mine.id) {
          handleSessionEndedRemotely({ idleTimeout });
        }
        return;
      }

      if (laborPresenceEventShouldEndLocalSession(hint, mine.id)) {
        handleSessionEndedRemotely({ idleTimeout });
        return;
      }

      const cachedPayload = readOpenLaborSessionPayloadFromCache(queryClient);
      if (openSessionPayloadEndsLocalSession(cachedPayload, mine.id) && sessionRef.current?.id === mine.id) {
        handleSessionEndedRemotely({ idleTimeout });
        return;
      }

      try {
        const openPayload = await fetchLaborOpenSession();
        const openRow = openSessionRowFromPayload(openPayload);
        if ((openRow === null || openRow.id !== mine.id) && sessionRef.current?.id === mine.id) {
          handleSessionEndedRemotely({ idleTimeout });
        }
      } catch {
        const fallbackPayload = readOpenLaborSessionPayloadFromCache(queryClient);
        if (openSessionPayloadEndsLocalSession(fallbackPayload, mine.id) && sessionRef.current?.id === mine.id) {
          handleSessionEndedRemotely({ idleTimeout });
        }
      }
    },
    [handleSessionEndedRemotely, queryClient],
  );

  const trackUserAttention = config.trackUserAttention !== false;
  useLaborAttentionActivity(touchActivity, session !== null && trackUserAttention);

  /**
   * Updates writes-blocked state/ref and heartbeat/idle config from an `/api/labor/active-session` payload.
   * Must run before `beginWorking()` on auto-start pages so the writes-blocked ref does not stay stuck on its
   * `useRef(true)` initial value, which would otherwise drop the user into the local-only timer branch and
   * write a `laborDevShellLock` that the new `LaborLockdownGate` interstitial then locks against.
   */
  const applyLaborSessionMetadata = useCallback((payload: LaborActiveSessionPayload): void => {
    if (typeof payload.heartbeat_interval_ms === "number" && Number.isFinite(payload.heartbeat_interval_ms)) {
      setHeartbeatIntervalMs(payload.heartbeat_interval_ms);
    }
    if (typeof payload.idle_timeout_ms === "number" && Number.isFinite(payload.idle_timeout_ms)) {
      setIdleTimeoutMs(payload.idle_timeout_ms);
    }
    const blocked = responseWritesBlocked(payload as Record<string, unknown>);
    if (blocked !== undefined) setLaborWritesBlocked(blocked);
    else setLaborWritesBlocked(false);
    writesBlockedRef.current =
      blocked === true ||
      !writesAllowedRef.current ||
      configRef.current.devPersistenceDisabled();
  }, []);

  const applyActivePayload = useCallback((payload: LaborActiveSessionPayload): LaborSession | null => {
    applyLaborSessionMetadata(payload);

    const otherPage = typeof payload.active_on_other_page === "string" ? payload.active_on_other_page.trim() : "";
    if (otherPage !== "") {
      setActiveOnOtherPage(otherPage);
      setBanner({
        tone: "warning",
        message: `Finish clocking out of ${formatLaborLocationLabel(otherPage)} before starting here.`,
      });
      setSession(null);
      setScans([]);
      mirrorPersistedOpenSession(null);
      return null;
    }
    setActiveOnOtherPage(null);

    const nextSession = parseLaborSessionRow(payload.session);
    if (nextSession && payload.was_stale !== true) {
      if (nextSession.id > 0) {
        clearLaborDevShellLock();
      }
      setSession(nextSession);
      setScans(Array.isArray(payload.scans) ? payload.scans : []);
      if (nextSession.id > 0) {
        mirrorPersistedOpenSession(nextSession);
      }
      touchActivity();
      setLastSummary(null);
      return nextSession;
    }
    if (nextSession && payload.was_stale === true) {
      setLastSummary({
        netSeconds: nextSession.net_seconds ?? 0,
        idleTimeout: true,
        deviceCount: nextSession.device_count,
      });
    }
    setSession(null);
    setScans([]);
    mirrorPersistedOpenSession(null);
    return null;
  }, [applyLaborSessionMetadata, mirrorPersistedOpenSession, touchActivity]);

  const beginWorking = useCallback(async (): Promise<void> => {
    clearLaborReadOnlyStay();
    touchActivity();
    const currentConfig = configRef.current;
    if (currentConfig.devPersistenceDisabled()) {
      // Best-effort stale server cleanup must not block the dev local timer gate.
      void endOpenLaborServerSessionIfAny();
      setSession({ id: -1, started_at: new Date().toISOString(), page: currentConfig.page });
      setLaborDevShellLock({ page: currentConfig.page });
      setLastSummary(null);
      setBanner({
        tone: "success",
        message: formatLocalOnlyMessage(currentConfig, "dev"),
        badgeLabel: "Dev mode",
      });
      setGateReady(laborGateOpenForConfiguredPersistence(false));
      return;
    }
    if (!writesAllowedRef.current || writesBlockedRef.current) {
      const readOnly = !writesAllowedRef.current;
      setSession({ id: -1, started_at: new Date().toISOString(), page: currentConfig.page });
      setLaborDevShellLock({ page: currentConfig.page });
      setLastSummary(null);
      setBanner({
        tone: "success",
        message: formatLocalOnlyMessage(currentConfig, readOnly ? "read-only" : "writes-blocked"),
        badgeLabel: readOnly ? "Read-only" : "No labor writes",
      });
      setGateReady(laborGateOpenForConfiguredPersistence(false));
      return;
    }
    try {
      const laborTabId = getLaborTabId();
      const maxStartAttempts = 6;
      for (let attempt = 0; attempt < maxStartAttempts; attempt++) {
        if (isLaborOwnerDeparting()) {
          await waitForLaborOwnerDepartureComplete();
        }
        const response = await startLaborSession({
          page: currentConfig.page,
          domain: currentConfig.domain,
          tabId: laborTabId === LABOR_TAB_ID_NO_STORAGE ? undefined : laborTabId,
        });
        const record = asRecord(response);
        if (responseWritesBlocked(record) === true) {
          setLaborWritesBlocked(true);
          writesBlockedRef.current = true;
          setSession({ id: -1, started_at: new Date().toISOString(), page: currentConfig.page });
          setLaborDevShellLock({ page: currentConfig.page });
          setBanner({
            tone: "success",
            message: formatLocalOnlyMessage(currentConfig, "writes-blocked"),
            badgeLabel: "No labor writes",
          });
          setGateReady(laborGateOpenForConfiguredPersistence(false));
          return;
        }
        if (record?.error === "active_on_other_page") {
          if (isLaborOwnerDeparting() || attempt < maxStartAttempts - 1) {
            await waitForLaborOwnerDepartureComplete();
            continue;
          }
          const otherPage =
            typeof record.page === "string" && record.page.trim() !== "" ? record.page.trim() : "another page";
          setBanner({
            tone: "warning",
            message: `Finish clocking out of ${formatLaborLocationLabel(otherPage)} before starting here.`,
          });
          setGateReady(false);
          return;
        }
        const nextSession = parseLaborSessionRow(response.session);
        if (nextSession === null) {
          setBanner({ tone: "warning", message: "Server did not return an active labor session row." });
          setGateReady(false);
          return;
        }
        setSession(nextSession);
        setScans([]);
        setLastSummary(null);
        if (nextSession.id > 0) {
          mirrorPersistedOpenSession(nextSession);
        }
        setBanner(currentConfig.inactiveStartMessage ? { tone: "success", message: currentConfig.inactiveStartMessage } : null);
        setLaborWritesBlocked(false);
        writesBlockedRef.current = false;
        if (nextSession.id > 0) {
          clearLaborDevShellLock();
        }
        setGateReady(laborGateOpenForConfiguredPersistence(nextSession.id > 0));
        invalidate();
        return;
      }
    } catch (error) {
      setBanner({
        tone: "warning",
        message: error instanceof ApiError ? error.message : "Could not start labor session.",
      });
      setGateReady(false);
    }
  }, [invalidate, laborGateOpenForConfiguredPersistence, mirrorPersistedOpenSession, touchActivity]);

  const finalize = useCallback(async (idleTimeout: boolean): Promise<void> => {
    const current = sessionRef.current;
    if (current === null) return;
    const currentConfig = configRef.current;
    const startedIso = current.started_at;
    const sessionId = current.id;
    let serverSummary: { net_seconds?: number; device_count?: number } | null = null;

    if (sessionId > 0 && !writesBlockedRef.current && !currentConfig.devPersistenceDisabled()) {
      try {
        const ended = await endLaborSession({
          sessionId,
          idleTimeout,
          domain: currentConfig.domain,
        });
        serverSummary = {
          net_seconds: numberFromUnknown(ended.net_seconds) ?? undefined,
          device_count: numberFromUnknown(ended.device_count) ?? undefined,
        };
      } catch (error) {
        if (
          isLaborOwnerDeparting()
          || isLaborSessionTombstoned(sessionId)
        ) {
          sessionRef.current = null;
          setSession(null);
          setScans([]);
          mirrorPersistedOpenSession(null);
          invalidate();
          if (import.meta.env.DEV) {
            clearLaborDevCountdown(currentConfig.page);
          }
          return;
        }
        setBanner({
          tone: "warning",
          message: error instanceof ApiError ? error.message : "Could not close the labor session on the server.",
        });
        return;
      }
    }

    const localNetSeconds = Math.max(0, Math.floor((Date.now() - new Date(startedIso).getTime()) / 1000));
    const netSeconds = serverSummary?.net_seconds ?? localNetSeconds;
    const deviceCount = serverSummary?.device_count ?? scans.length;
    if (sessionId < 0) {
      void endOpenLaborServerSessionIfAny();
      clearLaborDevShellLock();
    }
    if (sessionId > 0) {
      mirrorPersistedOpenSession(null);
    }
    setSession(null);
    setScans([]);
    setLastSummary({ netSeconds, idleTimeout, deviceCount });
    setBanner({
      tone: idleTimeout ? "warning" : "success",
      message: `${idleTimeout ? "Clocked out due to inactivity" : "Clocked out"}. ${netSeconds} seconds net time on ${currentConfig.workLabel}.`,
    });
    invalidate();
    if (import.meta.env.DEV) {
      clearLaborDevCountdown(currentConfig.page);
    }

    if (idleTimeout) {
      currentConfig.onIdleTimeout?.({ netSeconds });
      return;
    }

    currentConfig.onStopped?.();

    if (currentConfig.restartAfterManualStop) {
      await beginWorking();
    }
  }, [beginWorking, invalidate, mirrorPersistedOpenSession, scans.length]);

  useEffect(() => {
    finalizeRef.current = (idleTimeout: boolean) => {
      void finalize(idleTimeout);
    };
  }, [finalize]);

  const stopWorking = useCallback(async (): Promise<void> => {
    await finalize(false);
  }, [finalize]);

  const recordScan = useCallback(async (input: { deviceSerial: string; target: LaborTargetDescriptor }): Promise<void> => {
    const current = sessionRef.current;
    const currentConfig = configRef.current;
    touchActivity();
    if (current === null || current.id <= 0 || writesBlockedRef.current || currentConfig.devPersistenceDisabled()) {
      return;
    }
    await recordLaborScan({
      sessionId: current.id,
      deviceSerial: input.deviceSerial,
      target: input.target,
      domain: currentConfig.domain,
    });
  }, [touchActivity]);

  const resumeOpenSessionFromServer = useCallback(async (): Promise<boolean> => {
    const currentConfig = configRef.current;
    try {
      const payload = await fetchLaborOpenSession();
      const nextSession = parseLaborSessionRow(payload.session);
      if (nextSession === null || !laborPageKeysEquivalent(nextSession.page ?? "", currentConfig.page)) {
        return false;
      }
      setSession(nextSession);
      setScans([]);
      setLastSummary(null);
      if (nextSession.id > 0) {
        mirrorPersistedOpenSession(nextSession);
        clearLaborDevShellLock();
      }
      setGateReady(laborGateOpenForConfiguredPersistence(nextSession.id > 0));
      invalidate();
      return true;
    } catch {
      return false;
    }
  }, [invalidate, laborGateOpenForConfiguredPersistence, mirrorPersistedOpenSession]);

  useEffect(() => {
    let active = true;
    const currentConfig = configRef.current;
    const pageForCleanup = config.page;
    const persistedSnapshot = sessionRef.current;
    const previousPageSnapshot = pageRef.current;

    const devLocalSessionAlreadyActive =
      currentConfig.devPersistenceDisabled() &&
      persistedSnapshot !== null &&
      persistedSnapshot.id < 0 &&
      laborPageKeysEquivalent(persistedSnapshot.page ?? "", currentConfig.page) &&
      pageRef.current !== "" &&
      laborPageKeysEquivalent(pageRef.current, currentConfig.page);

    if (devLocalSessionAlreadyActive) {
      setLoading(false);
      setGateReady(laborGateOpenForConfiguredPersistence(false));
      return () => {
        active = false;
        if (import.meta.env.DEV) {
          clearLaborDevCountdown(pageForCleanup);
        }
      };
    }

    setLoading(true);
    setGateReady(false);
    setBanner(null);
    setActiveOnOtherPage(null);
    setSession(null);
    setScans([]);

    void (async () => {
      const bootstrapStartedAt = import.meta.env.DEV ? performance.now() : 0;
      if (authLoading) return;
      if (
        authSession?.user != null &&
        !meQuery.isFetched &&
        !currentConfig.devPersistenceDisabled()
      ) {
        return;
      }
      try {
        if (isLaborReadOnlyStayActive(currentConfig.page)) {
          setGateReady(false);
          return;
        }
        if (!currentConfig.devPersistenceDisabled()) {
          clearLaborDevShellLock();
        }

        // When `devPersistenceDisabled` is true, do not clear `laborDevShellLock` here: the lock must survive
        // until the user clocks out or a working-to-working handoff rewrites it, so navigating away still shows
        // the shell interstitial. `beginWorking()` below rewrites the lock for the new working page when needed.

        const isPageHandoff =
          previousPageSnapshot !== "" &&
          !laborPageKeysEquivalent(previousPageSnapshot, currentConfig.page) &&
          persistedSnapshot !== null &&
          persistedSnapshot.id > 0 &&
          !writesBlockedRef.current &&
          !currentConfig.devPersistenceDisabled() &&
          laborPageKeysEquivalent(persistedSnapshot.page ?? "", previousPageSnapshot);

        if (isPageHandoff) {
          try {
            await endLaborSession({
              sessionId: persistedSnapshot.id,
              idleTimeout: false,
              domain: currentConfig.domain,
            });
          } catch {
            /* server may have already closed */
          }
          if (!active) return;
          invalidate();
        }

        if (currentConfig.devPersistenceDisabled()) {
          setHeartbeatIntervalMs(FALLBACK_LABOR_HEARTBEAT_INTERVAL_MS);
          setIdleTimeoutMs(FALLBACK_LABOR_IDLE_TIMEOUT_MS);
          if (currentConfig.autoStart) {
            await beginWorking();
          } else {
            setGateReady(laborGateOpenForConfiguredPersistence(true));
          }
          return;
        }
        const openPayload = queryClient.getQueryData<LaborActiveSessionPayload>([...LABOR_OPEN_SESSION_QUERY_KEY]);
        const openSessionHint = parseLaborSessionRow(openPayload?.session ?? null);
        const laborTabId = getLaborTabId();
        const payload = await fetchLaborActiveSessionResolvingDeparture({
          page: currentConfig.page,
          domain: currentConfig.domain,
          fetchActiveSession: fetchLaborActiveSession,
          currentTabId: laborTabId,
          openSessionPage: openSessionHint?.page ?? null,
          openSessionTabId:
            typeof openSessionHint?.tab_id === "string" ? openSessionHint.tab_id.trim() : null,
        });
        if (!active) return;
        // Keep writes-blocked ref and heartbeat/idle config in sync with the server before any branching
        // (notably before the auto-start fall-through to `beginWorking()`). Without this, the stale
        // `useRef(true)` initial value sends users into the local-only timer path on Final QC, Pre-Pack,
        // and Pre-Encapsulation, which writes a `laborDevShellLock` that locks the rest of the hub.
        applyLaborSessionMetadata(payload);
        const preOtherPage = activeOnOtherPageFromPayload(payload);
        const payloadSession = parseLaborSessionRow(payload.session);
        const tabId = laborTabId;

        if (preOtherPage !== "") {
          applyActivePayload(payload);
          if (!active) return;
          setGateReady(false);
          return;
        }

        if (!currentConfig.autoStart) {
          applyActivePayload(payload);
          if (!active) return;
          setGateReady(laborGateOpenForConfiguredPersistence(true));
          return;
        }

        const adoptable =
          payloadSession !== null &&
          payloadSession.id > 0 &&
          laborPageKeysEquivalent(payloadSession.page ?? "", currentConfig.page) &&
          (tabId === LABOR_TAB_ID_NO_STORAGE ||
            payloadSession.tab_id == null ||
            (typeof payloadSession.tab_id === "string" && payloadSession.tab_id.trim() === "") ||
            payloadSession.tab_id === tabId);

        if (adoptable) {
          const adopted = applyActivePayload(payload);
          if (!active) return;
          if (adopted !== null) {
            setGateReady(laborGateOpenForConfiguredPersistence(adopted.id > 0));
            return;
          }
        }

        await beginWorking();
      } catch (error) {
        if (!active) return;
        setBanner({
          tone: "warning",
          message: error instanceof ApiError ? error.message : "Could not load labor session.",
        });
        setGateReady(currentConfig.requirePersistedClockIn !== true && !currentConfig.autoStart);
      } finally {
        if (active) {
          pageRef.current = configRef.current.page;
          setLoading(false);
          if (import.meta.env.DEV && bootstrapStartedAt > 0) {
            console.debug(
              `[labor] bootstrap ${configRef.current.page} ${(performance.now() - bootstrapStartedAt).toFixed(0)}ms`,
            );
          }
        }
      }
    })();

    return () => {
      active = false;
      if (import.meta.env.DEV) {
        clearLaborDevCountdown(pageForCleanup);
      }
    };
  }, [
    laborGateOpenForConfiguredPersistence,
    applyActivePayload,
    applyLaborSessionMetadata,
    authLoading,
    beginWorking,
    config.page,
    config.domain,
    config.autoStart,
    invalidate,
    queryClient,
    meQuery.isFetched,
    authSession?.user?.id,
    writesAllowed,
    laborPersistenceOverride,
  ]);

  useHubLaborPresenceStream({
    enabled: session != null && session.id > 0,
    onPresenceChange: (event) => {
      if (!isHubLaborPresenceEvent(event)) {
        return;
      }
      void reconcileRemoteOpenSession(event);
    },
  });

  useEffect(() => {
    if (session === null || session.id <= 0) {
      return;
    }
    const localSessionId = session.id;
    const openSessionQueryKey = [...LABOR_OPEN_SESSION_QUERY_KEY];
    return queryClient.getQueryCache().subscribe((event: QueryCacheNotifyEvent) => {
      if (event.type !== "updated") {
        return;
      }
      const updatedQueryKey = event.query.queryKey;
      if (
        updatedQueryKey.length !== openSessionQueryKey.length
        || updatedQueryKey.some((part: unknown, index: number) => part !== openSessionQueryKey[index])
      ) {
        return;
      }
      const payload = event.query.state.data as LaborActiveSessionPayload | undefined;
      if (!openSessionPayloadEndsLocalSession(payload, localSessionId)) {
        return;
      }
      if (sessionRef.current?.id !== localSessionId) {
        return;
      }
      handleSessionEndedRemotely();
    });
  }, [handleSessionEndedRemotely, queryClient, session?.id]);

  useEffect(() => {
    const reconcileRemoteClockOut = (storageKey: string | null): void => {
      if (
        storageKey !== null &&
        storageKey !== LABOR_DEV_SHELL_STORAGE_KEY &&
        storageKey !== LABOR_OPEN_SESSION_SYNC_STORAGE_KEY
      ) {
        return;
      }
      void reconcileRemoteOpenSession();
    };

    const onStorage = (event: StorageEvent): void => {
      reconcileRemoteClockOut(event.key);
    };

    const onOpenSessionSync = (): void => {
      void reconcileRemoteOpenSession();
    };

    window.addEventListener("storage", onStorage);
    const unsubscribeSync = subscribeLaborOpenSessionSync(onOpenSessionSync);
    return () => {
      window.removeEventListener("storage", onStorage);
      unsubscribeSync();
    };
  }, [reconcileRemoteOpenSession]);

  useEffect(() => {
    if (session === null) return;
    const cfg = configRef.current;
    const wall = Date.now();
    const heartbeatIso = session.last_heartbeat_at;
    const parsedDb = heartbeatIso ? Date.parse(heartbeatIso) : Number.NaN;
    lastDbHeartbeatAtMsRef.current = Number.isFinite(parsedDb) ? parsedDb : wall;
    lastActivityAcknowledgedMsRef.current = lastActivityMsRef.current;
    if (import.meta.env.DEV) {
      writeLaborDevCountdown({
        page: cfg.page,
        startedAtMs: new Date(session.started_at).getTime(),
        lastActivityMs: lastActivityMsRef.current,
        lastDbHeartbeatAtMs: lastDbHeartbeatAtMsRef.current,
        idleTimeoutMs,
        isWorking: true,
        updatedAtMs: wall,
      });
    }
  }, [session?.id, session?.started_at, session?.last_heartbeat_at, idleTimeoutMs]);

  useEffect(() => {
    heartbeatFailureStreakRef.current = 0;
    idleWarningPlayedRef.current = false;
  }, [session?.id]);

  useEffect(() => {
    if (session === null) return;
    const intervalMs = heartbeatIntervalMs;
    const idleMs = idleTimeoutMs;

    const tick = (): void => {
      const nowMs = Date.now();
      const activityMs = lastActivityMsRef.current;
      const hasPendingActivity = activityMs > lastActivityAcknowledgedMsRef.current;
      const idleSinceHeartbeatMs = nowMs - lastDbHeartbeatAtMsRef.current;
      const action = laborIdleTickAction({
        disableIdleTimeout: configRef.current.disableIdleTimeout === true,
        hasPendingActivity,
        idleSinceHeartbeatMs,
        idleTimeoutMs: idleMs,
        idleWarningAlreadyPlayed: idleWarningPlayedRef.current,
      });
      if (action === "warn") {
        idleWarningPlayedRef.current = true;
        playLaborIdleWarningSound();
        return;
      }
      if (action === "clock_out") {
        finalizeRef.current(true);
        return;
      }
      if (action === "wait") {
        return;
      }
      const current = sessionRef.current;
      const cfg = configRef.current;
      if (current === null) return;

      const flushLocal = (): void => {
        flushLocalLaborHeartbeatMirror({
          page: cfg.page,
          startedAtIso: current.started_at,
          idleTimeoutMs: idleMs,
          lastActivityMsRef,
          lastActivityAcknowledgedMsRef,
          lastDbHeartbeatAtMsRef,
        });
      };

      if (current.id > 0 && !writesBlockedRef.current && !cfg.devPersistenceDisabled()) {
        void sendLaborHeartbeat({ sessionId: current.id, domain: cfg.domain })
          .then((response) => {
            heartbeatFailureStreakRef.current = 0;
            const record =
              response && typeof response === "object" && !Array.isArray(response)
                ? (response as Record<string, unknown>)
                : null;
            if (record !== null && laborHeartbeatResponseSessionClosed(record)) {
              void reconcileRemoteOpenSession();
              return;
            }
            flushLocal();
          })
          .catch((error) => {
            heartbeatFailureStreakRef.current += 1;
            const currentSessionId = sessionRef.current?.id ?? 0;
            const sessionAlreadyEnded =
              isLaborOwnerDeparting()
              || (currentSessionId > 0 && isLaborSessionTombstoned(currentSessionId));
            if (error instanceof ApiError && error.status === 404) {
              void reconcileRemoteOpenSession();
              return;
            }
            const fatal = isFatalLaborHeartbeatError(error);
            const shouldRecover =
              cfg.requirePersistedClockIn === true || typeof cfg.onPersistedSessionLost === "function";
            if (shouldRecover && !sessionAlreadyEnded && fatal) {
              void handlePersistedSessionLostFromHeartbeat();
            }
          });
      } else {
        flushLocal();
      }
    };

    const onWakeOrOnline = (): void => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      if (lastActivityMsRef.current > lastActivityAcknowledgedMsRef.current) {
        tick();
      }
    };

    const intervalId = window.setInterval(tick, intervalMs);
    window.addEventListener("online", onWakeOrOnline);
    document.addEventListener("visibilitychange", onWakeOrOnline);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("online", onWakeOrOnline);
      document.removeEventListener("visibilitychange", onWakeOrOnline);
    };
  }, [handlePersistedSessionLostFromHeartbeat, heartbeatIntervalMs, idleTimeoutMs, reconcileRemoteOpenSession, session]);

  return {
    gateReady,
    isWorking: session !== null,
    loading,
    session,
    sessionId: session?.id && session.id > 0 ? session.id : null,
    scans,
    elapsedSeconds,
    heartbeatIntervalMs,
    idleTimeoutMs,
    banner,
    lastSummary,
    activeOnOtherPage,
    touchActivity,
    beginWorking,
    stopWorking,
    recordScan,
    resumeOpenSessionFromServer,
  };
}

